import cn from 'classnames'
import { Fragment, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'

import { HintComponent } from '../../../HintComponent'

import styles from './CategoryHeader.module.scss'

interface IProps {
  fyear: number
  translationKey: string
  isYearlyPlanning: boolean
  isShowCalcActuals: boolean
  isShowFutureYears: boolean
  currentFQuarter: number
  periodFQuarters?: number[]
  isQbrYtd?: boolean
}

export const CategoryHeader = ({
  fyear,
  translationKey,
  isYearlyPlanning,
  isShowCalcActuals,
  isShowFutureYears,
  currentFQuarter,
  periodFQuarters = [1, 2, 3, 4],
}: IProps) => {
  const periodFQuartersDefault = useMemo(() => [1, 2, 3, 4], [])
  const headerRow1Key = useMemo(() => `${translationKey}-header-row-1`, [translationKey])
  const headerRow2Key = useMemo(() => `${translationKey}-header-row-2`, [translationKey])
  const isShowYTD = useMemo(() => isShowCalcActuals, [isShowCalcActuals])
  const prevFyColSpan = useMemo(
    () => (isYearlyPlanning ? 0 : periodFQuartersDefault.length) + 1 + (isShowCalcActuals ? 1 : 0),
    [isShowCalcActuals, isYearlyPlanning, periodFQuartersDefault.length]
  )
  const currFyColSpan = useMemo(
    () => (isYearlyPlanning ? 0 : periodFQuarters.length) + 1,
    [isYearlyPlanning, periodFQuarters.length]
  )
  const fy = useMemo(() => fyear % 100, [fyear])

  return (
    <>
      <tr key={headerRow1Key}>
        <th className={styles.quarterCell}></th>
        <th className={cn(styles.quarterCell, styles.colSeparator2)}>
          <div className={styles.quarterContainer}>FY{fy - 2}</div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)} colSpan={prevFyColSpan}>
          <div className={styles.quarterContainer}>FY{fy - 1}</div>
        </th>
        <th className={cn(styles.quarterCell, styles.colSeparator)} colSpan={currFyColSpan}>
          <div className={styles.quarterContainer}>FY{fy}</div>
        </th>
        {isShowFutureYears && (
          <>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>FY{fy + 1}</div>
            </th>
            <th className={cn(styles.quarterCell, styles.colSeparator)}>
              <div className={styles.quarterContainer}>FY{fy + 2}</div>
            </th>
          </>
        )}
      </tr>

      <tr key={headerRow2Key}>
        <th className={styles.titleCell}>
          <Translation text="jbp-hrtargets-layer" span actionsInside />
        </th>

        {/** Prev-prev FYear */}

        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <div>Total</div>
          </div>
        </th>

        {/** Prev FYear */}

        {isYearlyPlanning ? (
          <></>
        ) : (
          periodFQuartersDefault.map((fq) => {
            const isShowQuarterYTD = isShowCalcActuals && fq >= currentFQuarter
            const key1 = `${headerRow2Key}-prev-fy-${fq}`
            const key2 = `${headerRow2Key}-prev-fy-${fq}-2`

            return (
              <Fragment key={key1}>
                <th key={key2} className={cn(styles.tableCell, styles.colSeparator)}>
                  <div className={styles.noWrapCell}>
                    <div>Q{fq}</div>
                    <div>{isShowQuarterYTD && <>&nbsp;YTD</>}</div>
                  </div>
                </th>
              </Fragment>
            )
          })
        )}
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <div>Total</div>
            <div>{isShowYTD && <>&nbsp;YTD</>}</div>
          </div>
        </th>
        {isShowCalcActuals && (
          <th className={cn(styles.tableCell, styles.colSeparator)}>
            <div className={styles.noWrapCell}>
              <div>Total*</div>
              <div className={styles.hintWrapper}>
                <HintComponent text="jbp-planning-calc-actuals-hint" />
              </div>
            </div>
          </th>
        )}

        {/** Current FYear */}

        {isYearlyPlanning ? (
          <></>
        ) : (
          periodFQuarters.map((fq) => {
            const key1 = `${headerRow2Key}-curr-fy-${fq}`
            const key2 = `${headerRow2Key}-curr-fy-${fq}-2`

            return (
              <Fragment key={key1}>
                <th key={key2} className={cn(styles.tableCell, styles.colSeparator)}>
                  <div className={styles.noWrapCell}>
                    <div>Q{fq}</div>
                  </div>
                </th>
              </Fragment>
            )
          })
        )}
        <th className={cn(styles.tableCell, styles.colSeparator)}>
          <div className={styles.noWrapCell}>
            <div>Total</div>
          </div>
        </th>

        {/** Future FYear */}

        {isShowFutureYears && (
          <>
            <th className={cn(styles.tableCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <div>Total</div>
              </div>
            </th>
            <th className={cn(styles.tableCell, styles.colSeparator)}>
              <div className={styles.noWrapCell}>
                <div>Total</div>
              </div>
            </th>
          </>
        )}
      </tr>
    </>
  )
}
